import React, { useState, useEffect } from 'react';

export function ScrollBallerina() {
    const [danceClass, setDanceClass] = useState('');
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        let timeoutId;
        let lastTime = Date.now();
        let scrollAccumulator = 0;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            const currentTime = Date.now();
            const timeDiff = currentTime - lastTime;
            const scrollDiff = Math.abs(currentScrollY - lastScrollY);
            const currentScrollSpeed = scrollDiff / timeDiff;

            // Accumulate scroll distance for animation triggering
            if (!isAnimating) {
                scrollAccumulator += scrollDiff;
            }

            setLastScrollY(currentScrollY);
            lastTime = currentTime;

            // Don't trigger new animations if one is already playing
            if (isAnimating) return;

            // Only trigger animation if there's significant accumulated scroll movement
            if (scrollAccumulator < 15) return;

            let newDanceClass = '';
            let animationDuration = 0;

            // Determine dance move based on scroll behavior
            if (currentScrollSpeed > 3) {
                // Fast scrolling - dramatic leap
                newDanceClass = 'ballerina-leap';
                animationDuration = 1200; // 1.2s
            } else if (scrollAccumulator > 120) {
                // Long accumulated scroll - elegant pirouette
                newDanceClass = 'ballerina-pirouette';
                animationDuration = 2000; // 2s
            } else if (scrollAccumulator > 15) {
                // Normal scroll - graceful dance
                newDanceClass = 'ballerina-dancing';
                animationDuration = 1500; // 1.5s
            }

            if (newDanceClass) {
                // Reset accumulator and start animation
                scrollAccumulator = 0;
                setIsAnimating(true);
                setDanceClass(newDanceClass);

                // Clear the animation after it completes
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    setDanceClass('');
                    setIsAnimating(false);
                    // Reset scroll tracking for next animation
                    scrollAccumulator = 0;
                }, animationDuration);
            }
        };

        // Throttle scroll events for performance
        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', throttledScroll);
            clearTimeout(timeoutId);
        };
    }, []); // Remove dependencies to prevent unnecessary re-renders

    return (
        <div className={`scroll-ballerina ${danceClass}`}>
            <svg 
                className="ballerina-icon" 
                viewBox="0 0 100 100" 
                fill="currentColor"
                aria-label="Dancing ballerina"
            >
                {/* Graceful ballerina in arabesque pose */}
                
                {/* Head and hair */}
                <circle cx="50" cy="15" r="6" fill="currentColor"/>
                <path d="M 44 12 Q 40 8 42 6 Q 48 4 54 8 Q 58 10 56 14 Q 54 16 50 15" 
                      fill="currentColor" opacity="0.8"/>
                
                {/* Body and torso */}
                <ellipse cx="50" cy="30" rx="4" ry="8" fill="currentColor"/>
                
                {/* Arms in elegant ballet position */}
                <path d="M 46 25 Q 35 20 30 25 Q 28 27 30 29 Q 35 32 45 28" 
                      stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <path d="M 54 25 Q 65 22 70 28 Q 72 30 70 32 Q 65 35 55 30" 
                      stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                
                {/* Hands */}
                <circle cx="29" cy="27" r="1.5" fill="currentColor"/>
                <circle cx="71" cy="30" r="1.5" fill="currentColor"/>
                
                {/* Tutu - multiple layers for volume */}
                <ellipse cx="50" cy="38" rx="12" ry="4" fill="currentColor" opacity="0.3"/>
                <ellipse cx="50" cy="40" rx="10" ry="3" fill="currentColor" opacity="0.4"/>
                <ellipse cx="50" cy="42" rx="8" ry="2.5" fill="currentColor" opacity="0.5"/>
                <ellipse cx="50" cy="44" rx="6" ry="2" fill="currentColor" opacity="0.6"/>
                
                {/* Tutu details - decorative elements */}
                <path d="M 38 38 Q 42 36 46 38 Q 50 36 54 38 Q 58 36 62 38" 
                      stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.6"/>
                <path d="M 40 40 Q 44 38 48 40 Q 52 38 56 40 Q 60 38 60 40" 
                      stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.5"/>
                
                {/* Supporting leg */}
                <line x1="50" y1="45" x2="48" y2="65" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="48" y1="65" x2="46" y2="85" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                
                {/* En pointe foot */}
                <ellipse cx="45" cy="87" rx="3" ry="1.5" fill="currentColor"/>
                <path d="M 42 87 Q 44 85 48 87" stroke="currentColor" strokeWidth="0.5" fill="none"/>
                
                {/* Extended leg in arabesque */}
                <line x1="52" y1="45" x2="65" y2="35" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="65" y1="35" x2="78" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                
                {/* Extended foot */}
                <ellipse cx="80" cy="29" rx="2.5" ry="1" fill="currentColor"/>
                <path d="M 77 29 Q 79 27 82 29" stroke="currentColor" strokeWidth="0.5" fill="none"/>
                
                {/* Flowing costume details */}
                <path d="M 62 42 Q 68 45 65 50 Q 62 48 60 45" 
                      fill="currentColor" opacity="0.3"/>
                <path d="M 38 42 Q 32 45 35 50 Q 38 48 40 45" 
                      fill="currentColor" opacity="0.3"/>
                
                {/* Hair ribbon/bow */}
                <path d="M 48 10 Q 50 8 52 10 Q 50 12 48 10" 
                      fill="currentColor" opacity="0.7"/>
                
                {/* Graceful flowing lines for movement */}
                <path d="M 30 25 Q 25 20 20 25" 
                      stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.4"/>
                <path d="M 70 28 Q 75 23 80 28" 
                      stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.4"/>
                <path d="M 78 30 Q 83 28 85 32" 
                      stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.3"/>
                
                {/* Ballet slipper ribbons */}
                <path d="M 45 85 Q 43 82 45 80" 
                      stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <path d="M 47 85 Q 49 82 47 80" 
                      stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <path d="M 80 30 Q 78 27 80 25" 
                      stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
                <path d="M 82 30 Q 84 27 82 25" 
                      stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.6"/>
            </svg>
        </div>
    );
} 